import styled, { css } from "styled-components";

import { PageSection } from "../../components/layout/layout-styles";
import { Heading as HeadingStyles } from "../../components/common/Typography/type-styles";
import {
  breakpoints,
  fontFamily,
  colors,
  sectionRhythm,
  spacing,
} from "../../../theme";

const FEATURE_PLACEMENT = [
  css`
    grid-column: 1 / span 2;
  `,
  css`
    grid-column: 3 / span 2;
  `,
  css`
    grid-column: 5 / span 2;
  `,
  css`
    grid-column: 2 / span 2;
    margin-top: -12px;

    @media only screen and (min-width: ${breakpoints.desktop}) {
      margin-top: -20px;
    }
  `,
  css`
    grid-column: 4 / span 2;
    margin-top: -12px;

    @media only screen and (min-width: ${breakpoints.desktop}) {
      margin-top: -20px;
    }
  `,
];

export const PageWrap = styled(PageSection)`
  background-color: ${colors.white};
`;

export const Content = styled.div`
  width: 94%;
  max-width: 1040px;
  margin: 0 auto;
`;

export const PageHero = styled.header`
  display: grid;
  gap: ${spacing(6)};
  margin-bottom: ${sectionRhythm.gap.mobile}px;
  grid-template-areas:
    "text"
    "visual";

  @media only screen and (min-width: ${breakpoints.desktop}) {
    grid-template-columns: 1.1fr 0.9fr;
    grid-template-areas: "text visual";
    align-items: center;
    gap: ${spacing(10)};
    margin-bottom: ${sectionRhythm.gap.desktop}px;
  }
`;

export const PageHeroText = styled.div`
  grid-area: text;
`;

export const PageHeroVisual = styled.div`
  grid-area: visual;
  height: 256px;
  width: max-content;
  max-width: 100%;
  margin: 0 auto;
  overflow: hidden;
  user-select: none;
  pointer-events: none;

  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  mask-image: linear-gradient(
    to top,
    rgba(0, 0, 0, 0.2) 20%,
    rgba(0, 0, 0, 1) 80%
  );
  -webkit-mask-image: linear-gradient(
    to top,
    rgba(0, 0, 0, 0.2) 20%,
    rgba(0, 0, 0, 1) 80%
  );

  img {
    display: block;
    width: 302px;
    height: max-content;
    max-width: none;
  }

  @media only screen and (min-width: ${breakpoints.desktop}) {
    height: 290px;
    margin-left: 0;
    margin-right: 0;

    img {
      width: auto;
      height: max-content;
    }
  }
`;

export const PageTitle = styled(HeadingStyles).attrs({ as: "h1" })`
  text-align: left;
  margin: 0 0 ${spacing(4)};
`;

export const PageLead = styled.p`
  margin: 0;
  font-size: 1.1rem;
  line-height: 1.35;
  color: ${colors.grey.dark};
  max-width: 36rem;

  @media only screen and (min-width: ${breakpoints.tablet}) {
    font-size: 1.25rem;
  }
`;

export const SectionsStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${sectionRhythm.gap.mobile}px;

  @media only screen and (min-width: ${breakpoints.desktop}) {
    gap: ${sectionRhythm.gap.desktop}px;
  }
`;

export const TechBlock = styled.section`
  position: relative;
`;

export const TechIconSlot = styled.div`
  position: absolute;
  top: -36px;
  right: 0;
  color: ${colors.black};

  @media only screen and (min-width: ${breakpoints.desktop}) {
    top: -8px;
    right: auto;
    left: 0;
    transform: translateX(calc(-100% - ${spacing(4)}));
  }
`;

export const InfoGrid = styled.div`
  display: grid;
  gap: ${spacing(8)};
  grid-template-areas:
    "title"
    "features"
    "cta";

  @media only screen and (min-width: ${breakpoints.desktop}) {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1.1fr);
    grid-template-areas:
      "title features"
      "cta features";
    column-gap: ${spacing(8)};
    row-gap: ${spacing(6)};
    align-items: start;
  }
`;

export const TitleSection = styled.div`
  grid-area: title;
`;

export const TechTitle = styled.h2`
  margin: 0;
  font-family: ${fontFamily.extraBold};
  line-height: 1.05;
  color: ${colors.brand.accentContent};

  ${(props) =>
    props.$size === "section"
      ? css`
          font-family: ${fontFamily.bold};
          font-size: 32px;
          line-height: 102%;

          @media only screen and (min-width: ${breakpoints.desktop}) {
            font-size: 40px;
          }
        `
      : css`
          font-size: 36px;

          @media only screen and (min-width: ${breakpoints.tablet}) {
            font-size: 48px;
          }

          @media only screen and (min-width: ${breakpoints.desktop}) {
            font-size: 56px;
          }
        `}
`;

export const TechDescription = styled.p`
  margin: ${spacing(5)} 0 0;
  font-family: ${fontFamily.medium};
  font-size: 1.05rem;
  line-height: 1.35;
  color: ${colors.grey.darkMedium};

  @media only screen and (min-width: ${breakpoints.tablet}) {
    font-size: 1.15rem;
  }
`;

export const FeaturesGrid = styled.div`
  grid-area: features;
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  width: min(100%, 302px);
  margin: 0 auto;

  @media only screen and (min-width: ${breakpoints.desktop}) {
    width: min(100%, 548px);
    margin: 0;
  }
`;

export const FeatureBubble = styled.div`
  position: relative;
  display: flex;
  aspect-ratio: 1;
  width: 100%;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: 50%;
  ${(props) => FEATURE_PLACEMENT[props.$index] || ""}
`;

export const FeatureBubbleFill = styled.div`
  position: absolute;
  inset: 1px;
  border-radius: 50%;
  background: linear-gradient(
    135deg,
    ${colors.brand.accentLight} 0%,
    ${colors.surface} 45%,
    ${colors.brand.accent} 100%
  );
`;

export const FeatureBubbleRing = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 1px solid
    color-mix(in srgb, ${colors.brand.accentContent} 18%, transparent);
  background: color-mix(in srgb, ${colors.white} 35%, transparent);
  backdrop-filter: blur(8px);
`;

export const FeatureBubbleText = styled.p`
  position: relative;
  z-index: 1;
  margin: 0 ${spacing(2)};
  text-align: center;
  font-family: ${fontFamily.bold};
  font-size: 0.7rem;
  line-height: 1.1;
  color: ${colors.brand.accentContent};

  @media only screen and (min-width: ${breakpoints.desktop}) {
    margin: 0 ${spacing(5)};
    font-size: 1.05rem;
  }
`;

export const CtaSlot = styled.div`
  grid-area: cta;
  display: flex;
  justify-content: center;

  @media only screen and (min-width: ${breakpoints.desktop}) {
    justify-content: flex-start;
  }
`;

export const ProjectsBlock = styled.div`
  margin-top: ${sectionRhythm.gap.mobile}px;

  @media only screen and (min-width: ${breakpoints.desktop}) {
    margin-top: ${sectionRhythm.gap.desktop}px;
  }
`;

export const ProjectsHeading = styled.h3`
  margin: 0 0 ${spacing(4)};
  font-family: ${fontFamily.bold};
  line-height: 102%;
  color: ${colors.brand.accentContent};

  ${(props) =>
    props.$size === "subsection"
      ? css`
          font-size: 24px;

          @media only screen and (min-width: ${breakpoints.desktop}) {
            font-size: 28px;
            margin-bottom: ${spacing(5)};
          }
        `
      : css`
          font-size: 32px;

          @media only screen and (min-width: ${breakpoints.desktop}) {
            font-size: 40px;
            margin-bottom: ${spacing(5)};
          }
        `}
`;

export const PortfolioGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${spacing(4)};

  @media only screen and (min-width: ${breakpoints.desktop}) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: ${spacing(5)};
  }
`;

export const NoteBlock = styled.div`
  margin-top: ${sectionRhythm.gap.mobile}px;

  @media only screen and (min-width: ${breakpoints.desktop}) {
    margin-top: ${sectionRhythm.gap.desktop}px;
  }
`;

export const NoteHeading = styled.h2`
  margin: 0 0 ${spacing(4)};
  font-family: ${fontFamily.bold};
  font-size: 32px;
  line-height: 102%;
  color: ${colors.brand.accentContent};

  @media only screen and (min-width: ${breakpoints.desktop}) {
    font-size: 40px;
  }
`;

export const NoteBody = styled.p`
  margin: 0;
  font-size: 1rem;
  line-height: 1.6;
  color: #374151;

  @media only screen and (min-width: ${breakpoints.tablet}) {
    font-size: 1.05rem;
  }
`;

export const NoteList = styled.ul`
  margin: 0;
  padding-left: 1.25rem;
  font-size: 1rem;
  line-height: 1.6;
  color: #374151;

  li + li {
    margin-top: ${spacing(2)};
  }

  @media only screen and (min-width: ${breakpoints.tablet}) {
    font-size: 1.05rem;
  }
`;
